import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { capture } from "@pontimed/analytics/client";
import { useSession } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import {
  Building2,
  Check,
  ChevronDown,
  Copy,
  Download,
  Gift,
  Loader2,
  LogIn,
  Mail,
  MessageCircle,
  QrCode,
  Stethoscope,
  User,
  Wallet,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useMemo, useRef, useState } from "react";

import { useAuthModal } from "@/components/auth/auth-modal-context";
import { useReferralShare } from "@/components/referral/referral-share-context";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import {
  buildReferralLink,
  dispatchReferralToggled,
  useReferralProgram,
} from "@/lib/hooks/use-referral-program";
import { showToast } from "@/lib/ui/toast";

type Audience = "user" | "provider" | "business";

export function ReferralShareSheet() {
  const { t } = useLingui();
  const { isOpen, config, closeReferralShare } = useReferralShare();
  const { user } = useSession();
  const { isActive, referralCode, loading } = useReferralProgram();
  const { open: openAuthModal } = useAuthModal();

  // Code returned by the activation POST. The hook refetch that follows is a
  // round trip away, so this flips the sheet to the link immediately instead of
  // leaving the user looking at the button they just pressed. Cleared when the
  // hook catches up (it then reports the same code).
  const [justActivatedCode, setJustActivatedCode] = useState<string | null>(null);
  const effectiveCode = referralCode ?? justActivatedCode;
  const effectiveActive = isActive || justActivatedCode !== null;

  // The "View earnings" footer link routes to /account/user/wallet. When the
  // sheet is opened from the wallet itself it's a no-op redirect, so we hide
  // the link. Source key convention: wallet-* (overview, referrals-empty,
  // commissions, …) — match by prefix.
  const hideEarningsLink = (config.source ?? "").startsWith("wallet");

  return (
    <ResponsiveSheet
      open={isOpen}
      onClose={closeReferralShare}
      width="lg"
      ariaLabel={t`Refer & earn`}
      hideCloseButton={false}
    >
      <div className="flex flex-col gap-5 pt-2">
        <Header />

        {loading ? (
          <LoadingState />
        ) : !user ? (
          <NotLoggedIn onLogin={() => openAuthModal()} />
        ) : !effectiveActive || !effectiveCode ? (
          <NotActive onActivated={setJustActivatedCode} />
        ) : (
          <ActiveState
            referralCode={effectiveCode}
            onClose={closeReferralShare}
            hideEarningsLink={hideEarningsLink}
          />
        )}
      </div>
    </ResponsiveSheet>
  );
}

function Header() {
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <div className="flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/15 to-teal-500/15">
        <Gift className="size-6 text-violet-600 dark:text-violet-400" />
      </div>
      <h2 className="font-heading text-foreground text-lg font-semibold">
        <Trans>Refer &amp; earn</Trans>
      </h2>
      <p className="text-muted-foreground max-w-xs text-xs">
        <Trans>
          Share your link with doctors, clinics and businesses. When they subscribe, you earn a
          commission for up to 3 years.
        </Trans>
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3">
      <div className="bg-muted h-14 animate-pulse rounded-xl" />
      <div className="bg-muted h-10 animate-pulse rounded-full" />
      <div className="grid grid-cols-4 gap-2">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-muted h-[76px] animate-pulse rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function NotLoggedIn({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="bg-muted/40 flex flex-col items-center gap-3 rounded-xl border border-dashed p-6 text-center">
      <LogIn className="text-muted-foreground size-6" />
      <div>
        <p className="text-foreground text-sm font-medium">
          <Trans>Sign in to recommend PontiMed</Trans>
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          <Trans>Your personal referral link unlocks once you're signed in.</Trans>
        </p>
      </div>
      <button
        type="button"
        onClick={onLogin}
        className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex min-h-[44px] items-center justify-center rounded-lg px-4 text-sm font-medium"
      >
        <Trans>Sign in</Trans>
      </button>
    </div>
  );
}

/**
 * Opt-in state. It used to link to /account/user/wallet — which is the page most
 * users open this sheet FROM, so the only path to a share link was a no-op
 * redirect plus hunting for an unlabelled switch (dead end recorded in
 * _docs/product-analysis-referrals-subscriptions-2026-09-10.md §3.1). It now
 * activates the program in place; the hook refetches on `referral-toggled` and
 * this state is replaced by the link + channels, in the same sheet.
 */
function NotActive({ onActivated }: { onActivated: (code: string) => void }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [activating, setActivating] = useState(false);

  const activate = useCallback(async () => {
    setActivating(true);
    const toast = showToast(t`Activating…`, "loading");
    try {
      const res = await apiFetch.post<{ referralCode?: string | null }>(
        "/api/account/wallet/toggle-referral",
        { enabled: true },
      );
      toast.update(t`Referral program active`, "success");
      // Wallet header switch + balance card read this through react-query;
      // the hook behind this sheet listens for the event.
      queryClient.invalidateQueries({ queryKey: ["api", "/api/account/wallet"] });
      dispatchReferralToggled();
      if (res?.referralCode) {
        // Straight to the link — no waiting on the refetch.
        onActivated(res.referralCode);
        return;
      }
      // Activated but no code came back (older API): let the refetch decide and
      // keep the button usable so the user is never stuck on a dead spinner.
      setActivating(false);
    } catch {
      toast.update(t`Could not activate the referral program.`, "error");
      setActivating(false);
    }
  }, [onActivated, queryClient, t]);

  return (
    <div className="bg-muted/40 flex flex-col items-center gap-3 rounded-xl border border-dashed p-6 text-center">
      <Gift className="text-muted-foreground size-6" />
      <div>
        <p className="text-foreground text-sm font-medium">
          <Trans>Referral program isn't active yet</Trans>
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          <Trans>
            Turn it on and your personal link is ready right here. You can turn it off any time.
          </Trans>
        </p>
      </div>
      <button
        type="button"
        onClick={activate}
        disabled={activating}
        className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium disabled:opacity-70"
      >
        {activating && <Loader2 className="size-4 animate-spin" />}
        <Trans>Activate and get your link</Trans>
      </button>
    </div>
  );
}

function ActiveState({
  referralCode,
  onClose,
  hideEarningsLink,
}: {
  referralCode: string;
  onClose: () => void;
  hideEarningsLink?: boolean;
}) {
  const { t } = useLingui();
  // Only a doctor's, clinic's or business's plan earns a commission
  // (2026-10-24), so the sheet starts on the doctor's message.
  const [audience, setAudience] = useState<Audience>("provider");
  const [copied, setCopied] = useState(false);
  const [qrExpanded, setQrExpanded] = useState(false);
  const qrContainerRef = useRef<HTMLDivElement>(null);

  // Bare marketing base URL + `?r=` — apps/web auto-detects locale/country and
  // the attribution hook captures the code on any landing path. Keeping the link
  // short makes it friendlier to share (chat, voice, print) and avoids leaking
  // the audience selector into the URL. Built by the SAME helper the hook uses
  // (`buildReferralLink`), so what the sheet shares and what `referralLink`
  // reports can never drift apart again.
  const referralLink = useMemo(() => buildReferralLink(referralCode), [referralCode]);

  // Share copy ported 1:1 from legacy `account.referralShare` (next-intl
  // catalog) so the wording, tone and per-audience formality match what
  // patients/doctors/businesses already received in production. EN source
  // strings here become the canonical msgids; per-locale translations live
  // verbatim in the .po catalogs.
  const messages = useMemo(
    () => ({
      user: {
        message: t`Hi! I'm using PontiMed to find the best doctors. Check it out: ${referralLink}`,
        subject: t`Find the best doctor with PontiMed`,
        body: t`Hi!\n\nI'm using PontiMed to find the best doctors in my area.\n\nCheck it out here: ${referralLink}\n\nIt's free!`,
      },
      provider: {
        message: t`Good day. I would like to recommend PontiMed — the platform that streamlines appointments and brings new patients to your practice. Synchronised calendar, significantly reduced administrative workload. Details: ${referralLink}`,
        subject: t`A recommendation for your practice — PontiMed`,
        body: t`Good day,\n\nI am writing to recommend PontiMed — the platform that organises appointments efficiently and brings new patients to your practice.\n\nKey benefits: patients from your area, Google / Apple Calendar sync, verified public profile, significantly reduced administrative time. Registration is free.\n\nI invite you to learn more: ${referralLink}\n\nKind regards`,
      },
      business: {
        message: t`Good day. I would like to recommend PontiMed for your business — your locations will be visible to thousands of patients searching for medical services. Details: ${referralLink}`,
        subject: t`A recommendation for your business — PontiMed`,
        body: t`Good day,\n\nI would like to recommend PontiMed — the medical platform patients use to easily find the services they need.\n\nA suitable solution for pharmacies, laboratories, medical imaging centres and analysis centres.\n\nI invite you to learn more: ${referralLink}\n\nKind regards`,
      },
    }),
    [t, referralLink],
  );
  const { message, subject, body } = messages[audience];

  // PostHog `referral_invited` — fired the moment the user commits to a
  // share channel (link copied, native share invoked, target app opened).
  // `audience` follows the legacy enum (user|provider|business); `channel`
  // is the surface they picked. Match of legacy
  // `src/components/referral/referral-share-sheet.tsx:190`.
  const trackInvite = useCallback(
    (channel: string) => {
      capture("referral_invited", { audience, channel });
    },
    [audience],
  );

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      showToast(t`Link copied`, "success");
      setTimeout(() => setCopied(false), 2000);
      trackInvite("copy");
    } catch {
      showToast(t`Couldn't copy link`, "error");
    }
  }, [referralLink, t, trackInvite]);

  const openUrl = useCallback((url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const shareWhatsApp = () => {
    trackInvite("whatsapp");
    openUrl(`https://wa.me/?text=${encodeURIComponent(message)}`);
  };
  const shareTelegram = () => {
    trackInvite("telegram");
    openUrl(
      `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(
        subject,
      )}`,
    );
  };
  const shareMessenger = () => {
    trackInvite("messenger");
    openUrl(`fb-messenger://share/?link=${encodeURIComponent(referralLink)}`);
  };
  const shareFacebook = () => {
    trackInvite("facebook");
    openUrl(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`);
  };
  const shareSMS = () => {
    trackInvite("sms");
    window.location.assign(`sms:?&body=${encodeURIComponent(message)}`);
  };
  const shareEmail = () => {
    trackInvite("email");
    window.location.assign(
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    );
  };

  // Instagram & TikTok have no public share URL. Open the native share sheet
  // (mobile) so the user can pick the app; on desktop, copy the link and
  // hint them to paste it in the Story / bio / caption.
  const shareViaNativeOrCopy = useCallback(
    async (hintKey: "instagram" | "tiktok") => {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        try {
          await navigator.share({ title: subject, text: message, url: referralLink });
          trackInvite(hintKey);
          return;
        } catch {
          // user cancelled or share failed — fall through to copy
        }
      }
      try {
        await navigator.clipboard.writeText(referralLink);
        showToast(
          hintKey === "instagram"
            ? t`Link copied — paste it in your Story or bio`
            : t`Link copied — paste it in your TikTok caption or bio`,
          "success",
        );
        trackInvite(hintKey);
      } catch {
        showToast(t`Couldn't copy link`, "error");
      }
    },
    [message, subject, referralLink, t, trackInvite],
  );

  const downloadQr = useCallback(() => {
    try {
      const svg = qrContainerRef.current?.querySelector("svg");
      if (!svg) return;
      const svgString = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const size = 512;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        canvas.toBlob((pngBlob) => {
          if (!pngBlob) return;
          const pngUrl = URL.createObjectURL(pngBlob);
          const a = document.createElement("a");
          a.href = pngUrl;
          a.download = "pontimed-referral-qr.png";
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(pngUrl);
        }, "image/png");
        URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        showToast(t`Couldn't download QR`, "error");
      };
      img.src = url;
    } catch {
      showToast(t`Couldn't download QR`, "error");
    }
  }, [t]);

  const tiles: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    bg: string;
    onClick: () => void;
  }> = [
    {
      key: "whatsapp",
      label: t`WhatsApp`,
      icon: <WhatsAppIcon className="size-5 text-white" />,
      bg: "bg-[#25D366]",
      onClick: shareWhatsApp,
    },
    {
      key: "messenger",
      label: t`Messenger`,
      icon: <MessengerIcon className="size-5 text-white" />,
      bg: "bg-gradient-to-br from-[#00B2FF] to-[#006AFF]",
      onClick: shareMessenger,
    },
    {
      key: "telegram",
      label: t`Telegram`,
      icon: <TelegramIcon className="size-5 text-white" />,
      bg: "bg-[#26A5E4]",
      onClick: shareTelegram,
    },
    {
      key: "facebook",
      label: t`Facebook`,
      icon: <FacebookIcon className="size-5 text-white" />,
      bg: "bg-[#1877F2]",
      onClick: shareFacebook,
    },
    {
      key: "instagram",
      label: t`Instagram`,
      icon: <InstagramIcon className="size-5 text-white" />,
      bg: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]",
      onClick: () => shareViaNativeOrCopy("instagram"),
    },
    {
      key: "tiktok",
      label: t`TikTok`,
      icon: <TikTokIcon className="size-5 text-white" />,
      bg: "bg-black",
      onClick: () => shareViaNativeOrCopy("tiktok"),
    },
    {
      key: "email",
      label: t`Email`,
      icon: <Mail className="size-5 text-white" />,
      bg: "bg-gradient-to-br from-teal-500 to-teal-600",
      onClick: shareEmail,
    },
    {
      key: "sms",
      label: t`SMS`,
      icon: <MessageCircle className="size-5 text-white" />,
      bg: "bg-gradient-to-br from-indigo-500 to-indigo-600",
      onClick: shareSMS,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <AudienceSelector value={audience} onChange={setAudience} />

      <div className="bg-muted/50 flex items-center gap-2 rounded-xl border p-2">
        <span className="text-muted-foreground truncate px-2 font-mono text-xs sm:text-sm">
          {referralLink}
        </span>
        <button
          type="button"
          onClick={copyLink}
          aria-label={t`Copy`}
          className="bg-primary text-primary-foreground hover:bg-primary/90 ml-auto inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-lg px-3 text-sm font-medium"
        >
          <AnimatePresence mode="wait" initial={false}>
            {copied ? (
              <m.span
                key="copied"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center gap-1.5"
              >
                <Check className="size-4" />
                <span className="text-xs font-medium">
                  <Trans>Copied</Trans>
                </span>
              </m.span>
            ) : (
              <m.span
                key="copy"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center gap-1.5"
              >
                <Copy className="size-4" />
                <span className="text-xs font-medium">
                  <Trans>Copy</Trans>
                </span>
              </m.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      <div>
        <p className="text-muted-foreground mb-2 px-1 text-[0.7rem] font-medium tracking-wide uppercase">
          <Trans>Share via</Trans>
        </p>
        <div className="grid grid-cols-4 gap-2">
          {tiles.map((tile, i) => (
            <m.button
              key={tile.key}
              type="button"
              onClick={tile.onClick}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03, duration: 0.2 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              className="bg-card hover:bg-muted flex min-h-[76px] flex-col items-center justify-center gap-1.5 rounded-xl border p-1.5 transition-colors"
            >
              <span
                className={cn(
                  "flex size-10 items-center justify-center rounded-full shadow-sm",
                  tile.bg,
                )}
              >
                {tile.icon}
              </span>
              <span className="text-foreground text-[0.65rem] font-medium">{tile.label}</span>
            </m.button>
          ))}
        </div>
      </div>

      <div className="bg-card rounded-xl border">
        <button
          type="button"
          onClick={() => setQrExpanded((v) => !v)}
          className="hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 py-2 text-left transition-colors"
          aria-expanded={qrExpanded}
        >
          <QrCode className="text-primary size-4 shrink-0" />
          <span className="text-foreground flex-1 text-sm font-medium">
            {qrExpanded ? <Trans>Hide QR code</Trans> : <Trans>Show QR code</Trans>}
          </span>
          <m.span
            animate={{ rotate: qrExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-muted-foreground"
          >
            <ChevronDown className="size-4" />
          </m.span>
        </button>

        <AnimatePresence initial={false}>
          {qrExpanded && (
            <m.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="flex flex-col items-center gap-3 border-t px-3 pt-4 pb-3">
                <div ref={qrContainerRef} className="rounded-lg bg-white p-3">
                  <QRCodeSVG value={referralLink} size={180} level="M" marginSize={0} />
                </div>
                <p className="text-muted-foreground text-center text-xs">
                  <Trans>Scan to open the referral link.</Trans>
                </p>
                <button
                  type="button"
                  onClick={downloadQr}
                  className="border-border bg-card hover:bg-muted inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-medium"
                >
                  <Download className="size-4" />
                  <Trans>Download QR (PNG)</Trans>
                </button>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </div>

      {!hideEarningsLink && (
        <Link
          to="/account/user/wallet"
          search={{ tab: "overview" }}
          onClick={onClose}
          className="text-primary hover:text-primary/80 inline-flex items-center justify-center gap-1.5 pt-1 text-xs font-medium"
        >
          <Wallet className="size-3.5" />
          <Trans>View earnings</Trans>
        </Link>
      )}
    </div>
  );
}

function AudienceSelector({
  value,
  onChange,
}: {
  value: Audience;
  onChange: (next: Audience) => void;
}) {
  const { t } = useLingui();
  const options: Array<{ id: Audience; label: string; icon: React.ReactNode }> = [
    { id: "user", label: t`User`, icon: <User className="size-3.5" /> },
    { id: "provider", label: t`Doctor`, icon: <Stethoscope className="size-3.5" /> },
    { id: "business", label: t`Medical business`, icon: <Building2 className="size-3.5" /> },
  ];

  return (
    <div>
      <p className="text-muted-foreground mb-1.5 px-1 text-[0.7rem] font-medium tracking-wide uppercase">
        <Trans>Who are you inviting?</Trans>
      </p>
      <div className="bg-muted relative grid grid-cols-3 gap-0.5 rounded-full p-0.5">
        {options.map((opt) => {
          const isActive = opt.id === value;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              className={cn(
                "relative z-10 flex min-h-[40px] items-center justify-center gap-1.5 rounded-full px-2 text-xs font-medium transition-colors",
                isActive ? "text-primary-foreground" : "text-foreground/70 hover:text-foreground",
              )}
            >
              {isActive && (
                <m.span
                  layoutId="audience-indicator"
                  transition={{ type: "spring", stiffness: 500, damping: 38 }}
                  className="bg-primary absolute inset-0 -z-10 rounded-full shadow-sm"
                />
              )}
              {opt.icon}
              <span className="truncate">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Brand icons (inline to avoid pulling a brand-icons lib)
// ---------------------------------------------------------------------------

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function MessengerIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.974 18.627 0 12 0zm1.193 14.963l-3.056-3.259-5.963 3.259L10.736 8l3.13 3.259L19.754 8l-6.561 6.963z" />
    </svg>
  );
}

function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.84-.1z" />
    </svg>
  );
}
